import React from 'react';
import clsx from 'clsx';
import {ListItemButton, type ListItemButtonProps} from '@mui/material';
import {listItemButtonClasses} from '@mui/material/ListItemButton';
import {
	composeClasses,
	styled,
	type WithClasses,
} from '@netx/core-components/Theme';
import {
	getVirtualizedListItemButtonUtilityClass,
	VIRTUALIZED_LIST_ITEM_BUTTON_CLASS_NAME,
	virtualizedListItemButtonClasses,
	type VirtualizedListItemButtonClasses,
} from './virtualizedListItemButtonClasses';

type VirtualizedListItemButtonOwnnerState = WithClasses<
	VirtualizedListItemButtonClasses,
	{
		isActive?: boolean;
	}
>;

const useUtilityClasses = (
	ownerState: VirtualizedListItemButtonOwnnerState,
) => {
	const {isActive, classes} = ownerState;
	const slots = {
		root: ['root', isActive && 'isActive'],
	};
	return composeClasses(
		slots,
		getVirtualizedListItemButtonUtilityClass,
		classes,
	);
};

const VirtualizedListItemButtonRoot = styled(ListItemButton, {
	name: VIRTUALIZED_LIST_ITEM_BUTTON_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	[`&.${listItemButtonClasses.dense}`]: {
		paddingTop: 2,
		paddingBottom: 2,
	},
	[`&.${virtualizedListItemButtonClasses.isActive}`]: {
		backgroundColor: theme.palette.action.hover,
		[`&.${listItemButtonClasses.selected}`]: {
			backgroundColor:
				theme.palette.mode === 'light'
					? `rgba(50, 127, 239, 0.12)`
					: `rgba(50, 127, 239, 0.24)`,
		},
	},
}));

export interface VirtualizedListItemButtonProps
	extends WithClasses<VirtualizedListItemButtonClasses, ListItemButtonProps> {
	isActive?: boolean;
}

export const VirtualizedListItemButtonPrimitive = (
	{
		classes: _classes,
		className,
		isActive,
		...props
	}: VirtualizedListItemButtonProps,
	ref: React.ForwardedRef<HTMLButtonElement>,
) => {
	const ownerState: VirtualizedListItemButtonOwnnerState = {
		classes: _classes,
		className,
		isActive,
	};

	const classes = useUtilityClasses(ownerState);

	return (
		<VirtualizedListItemButtonRoot
			ref={ref}
			className={clsx(classes.root, className)}
			tabIndex={isActive ? 0 : -1}
			{...props}
		/>
	);
};

export const VirtualizedListItemButton = React.forwardRef(
	VirtualizedListItemButtonPrimitive,
) as (props: VirtualizedListItemButtonProps) => JSX.Element;

export default VirtualizedListItemButton;
